import { fetchEventSource } from "@microsoft/fetch-event-source";
import { API_BASE, attemptRefresh, currentAccess } from "./api";
import { tokenStore } from "./tokens";
import type { IEvent } from "./proto";

export type StreamStatus = "connecting" | "open" | "closed";

export type StreamHandle = { close(): void };

export function openOpsStream(
  onEvent: (ev: IEvent) => void,
  opts: { onStatus?: (s: StreamStatus) => void } = {},
): StreamHandle {
  return openStream(`/ops/events/stream`, onEvent, opts);
}

export function openCustomerStream(
  onEvent: (ev: IEvent) => void,
  opts: { onStatus?: (s: StreamStatus) => void } = {},
): StreamHandle {
  return openStream(`/customer/events/stream`, onEvent, opts);
}

function openStream(
  path: string,
  onEvent: (ev: IEvent) => void,
  opts: { onStatus?: (s: StreamStatus) => void },
): StreamHandle {
  const url = `${API_BASE}${path}`;
  const ctrl = new AbortController();

  const authedFetch: typeof fetch = async (input, init) => {
    const send = async () => {
      const token = await currentAccess();
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    };
    const res = await send();
    if (res.status === 401 && (await attemptRefresh())) return send();
    return res;
  };

  opts.onStatus?.("connecting");

  void (async () => {
    try {
      await fetchEventSource(url, {
        signal: ctrl.signal,
        credentials: tokenStore.isNative ? "omit" : "include",
        openWhenHidden: true,
        fetch: authedFetch,
        async onopen(resp) {
          if (resp.ok) {
            opts.onStatus?.("open");
            return;
          }
          throw new Error(`SSE open ${resp.status}`);
        },
        onmessage(ev) {
          if (!ev.data) return;
          try {
            onEvent(JSON.parse(ev.data) as IEvent);
          } catch { /* skip malformed frame */ }
        },
        onerror() {
          opts.onStatus?.("connecting");
        },
        onclose() {
          opts.onStatus?.("closed");
        },
      });
    } catch {
      opts.onStatus?.("closed");
    }
  })();

  return {
    close() {
      ctrl.abort();
      opts.onStatus?.("closed");
    },
  };
}
